import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import openSocket from "../../services/socket-io";

import { makeStyles } from "@mui/styles";
import List from "@mui/material/List";
import Paper from "@mui/material/Paper";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";

import TicketListItem from "../TicketListItem";
import TicketsListSkeleton from "../TicketsListSkeleton";
import TransferTicketModal from "../TransferTicketModal";

import useTickets from "../../hooks/useTickets";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles(theme => ({
	ticketsListWrapper: {
		position: "relative",
		display: "flex",
		height: "100%",
		flexDirection: "column",
		overflow: "hidden",
		borderTopRightRadius: 0,
		borderBottomRightRadius: 0,
	},

	ticketsList: {
		flex: 1,
		overflowY: "scroll",
		...theme.scrollbarStyles,
		borderTop: "2px solid rgba(0, 0, 0, 0.12)",
	},

	ticketsListHeader: {
		color: "rgb(67, 83, 105)",
		zIndex: 2,
		backgroundColor: "white",
		borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
	},

	ticketsCount: {
		fontWeight: "normal",
		color: "rgb(104, 121, 146)",
		marginLeft: "8px",
		fontSize: "14px",
	},

	noTicketsText: {
		textAlign: "center",
		color: "rgb(104, 121, 146)",
		fontSize: "14px",
		lineHeight: "1.4",
	},

	noTicketsTitle: {
		textAlign: "center",
		fontSize: "16px",
		fontWeight: "600",
		margin: "0px",
	},

	noTicketsDiv: {
		display: "flex",
		height: "100px",
		margin: 40,
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
	},

	bulkBar: {
		display: "flex",
		alignItems: "center",
		gap: 8,
		padding: "4px 8px",
		borderBottom: "1px solid rgba(0,0,0,0.12)",
		backgroundColor: "rgba(243,112,33,0.06)",
		flexShrink: 0,
	},

	bulkCount: {
		fontSize: "0.8rem",
		color: "inherit",
		flex: 1,
	},
}));

const reducer = (state, action) => {
	if (action.type === "LOAD_TICKETS") {
		const newTickets = action.payload || [];

		newTickets.forEach(ticket => {
			const ticketIndex = state.findIndex(t => t.id === ticket.id);
			if (ticketIndex !== -1) {
				state[ticketIndex] = ticket;
				if (ticket.unreadMessages > 0) {
					state.unshift(state.splice(ticketIndex, 1)[0]);
				}
			} else {
				state.push(ticket);
			}
		});

		return [...state];
	}

	if (action.type === "RESET_UNREAD") {
		const ticketId = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state[ticketIndex].unreadMessages = 0;
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET") {
		const ticket = action.payload;

		const ticketIndex = state.findIndex(t => t.id === ticket.id);
		if (ticketIndex !== -1) {
			state[ticketIndex] = ticket;
		} else {
			state.unshift(ticket);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_UNREAD_MESSAGES") {
		const incoming = action.payload;

		const ticketIndex = state.findIndex(t => t.id === incoming.id);
		if (ticketIndex !== -1) {
			// Faz merge: preserva campos completos do objeto existente e aplica
			// apenas os campos do payload. Necessário para o evento ticketListUpdate
			// leve (que vem sem todos os campos) não apagar dados do ticket na lista.
			state[ticketIndex] = { ...state[ticketIndex], ...incoming };
			state.unshift(state.splice(ticketIndex, 1)[0]);
		} else if (incoming.contact) {
			// Só insere na lista se o payload for um ticket completo. O evento leve
			// ticketListUpdate manda um parcial SEM contact — inseri-lo criava um
			// item sem dados que derrubava o TicketListItem (ticket.contact.name).
			state.unshift(incoming);
		}

		return [...state];
	}

	if (action.type === "UPDATE_TICKET_CONTACT") {
		const contact = action.payload;
		const ticketIndex = state.findIndex(t => t.contactId === contact.id);
		if (ticketIndex !== -1) {
			state[ticketIndex].contact = contact;
		}
		return [...state];
	}

	if (action.type === "DELETE_TICKET") {
		const ticketId = action.payload;
		const ticketIndex = state.findIndex(t => t.id === ticketId);
		if (ticketIndex !== -1) {
			state.splice(ticketIndex, 1);
		}

		return [...state];
	}

	if (action.type === "RESET") {
		return [];
	}
};

	const TicketsList = (props) => {
		const { status, searchParam, showAll, selectedQueueIds, updateCount, style, unassigned, agentId } =
			props;
	const classes = useStyles();
	const [pageNumber, setPageNumber] = useState(1);
	const [ticketsList, dispatch] = useReducer(reducer, []);
	const { user } = useContext(AuthContext);
	const [selectedIds, setSelectedIds] = useState(new Set());
	const [bulkTransferOpen, setBulkTransferOpen] = useState(false);
	const [bulkLoading, setBulkLoading] = useState(false);

	const toggleSelect = useCallback((id) => {
		setSelectedIds(prev => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id); else next.add(id);
			return next;
		});
	}, []);

	const clearSelection = () => setSelectedIds(new Set());

	const handleBulkResolve = async () => {
		setBulkLoading(true);
		try {
			await Promise.all([...selectedIds].map(id =>
				api.put(`/tickets/${id}`, { status: "closed", userId: user?.id })
			));
			toast.success(`${selectedIds.size} ticket(s) resolvido(s).`);
			clearSelection();
		} catch (err) { toastError(err); }
		setBulkLoading(false);
	};

	useEffect(() => {
		dispatch({ type: "RESET" });
		setPageNumber(1);
	}, [status, searchParam, dispatch, showAll, selectedQueueIds, unassigned, agentId]);

	const { tickets, hasMore, loading, count } = useTickets({
		pageNumber,
		searchParam,
		status,
		showAll,
		queueIds: JSON.stringify(selectedQueueIds),
		unassigned,
		agentId,
	});

	useEffect(() => {
		if (!status && !searchParam) return;
		dispatch({
			type: "LOAD_TICKETS",
			payload: tickets,
		});
	}, [tickets]);

	useEffect(() => {
		const socket = openSocket();

		const shouldUpdateTicket = ticket => !searchParam &&
			!ticket.contact?.muted &&
			(!status || ticket.status === status) &&
			(unassigned
				? (!ticket.userId && ticket.status === "pending")
				: (!ticket.userId || ticket.userId === user?.id || showAll)) &&
			(!ticket.queueId || selectedQueueIds.indexOf(ticket.queueId) > -1);

		const notBelongsToThisList = ticket => {
			if (ticket.contact?.muted) return true;
			if (status && ticket.status !== status) return true;
			if (unassigned) return ticket.userId || ticket.status !== "pending";
			if (!showAll && ticket.userId && ticket.userId !== user?.id) return true;
			return false;
		};

		const notBelongsToUserQueues = ticket =>
			ticket.queueId && selectedQueueIds.indexOf(ticket.queueId) === -1;

		const handleConnect = () => {
			if (status) {
				socket.emit("joinTickets", status);
			} else {
				socket.emit("joinNotification");
			}
		};

		const handleTicket = data => {
			// A aba Buscar é estática — resultados não mudam via socket
			if (!status) return;
			if (data.action === "updateUnread") {
				dispatch({ type: "RESET_UNREAD", payload: data.ticketId });
			}
			// Um evento "update" pode chegar sem o objeto ticket (ex.: emissões
			// parciais do backend). Sem esta guarda, shouldUpdateTicket(undefined)
			// lê undefined.contact e derruba a tela inteira (ErrorBoundary).
			if (data.action === "update" && data.ticket && shouldUpdateTicket(data.ticket)) {
				dispatch({ type: "UPDATE_TICKET", payload: data.ticket });
			}
			if (data.action === "update" && data.ticket && (notBelongsToThisList(data.ticket) || notBelongsToUserQueues(data.ticket))) {
				dispatch({ type: "DELETE_TICKET", payload: data.ticket.id });
			}
			if (data.action === "delete") {
				dispatch({ type: "DELETE_TICKET", payload: data.ticketId });
			}
		};

		const handleAppMessage = data => {
			// A aba Buscar não recebe atualizações em tempo real
			if (!status) return;
			if (data.action === "create" && data.ticket && shouldUpdateTicket(data.ticket)) {
				dispatch({ type: "UPDATE_TICKET_UNREAD_MESSAGES", payload: data.ticket });
			}
		};

		// Evento leve: substitui o broadcast de appMessage para rooms de status.
		// Carrega só campos mínimos para atualizar badge e preview da lista.
		const handleTicketListUpdate = data => {
			if (!status) return;
			if (data.action === "updateUnread") {
				const partial = {
					id: data.ticketId,
					status: data.status,
					userId: data.userId,
					queueId: data.queueId,
					unreadMessages: data.unreadMessages,
					lastMessage: data.lastMessage,
				};
				if (shouldUpdateTicket(partial)) {
					dispatch({ type: "UPDATE_TICKET_UNREAD_MESSAGES", payload: partial });
				}
			}
		};

		const handleContact = data => {
			if (data.action === "update" && data.contact) {
				dispatch({ type: "UPDATE_TICKET_CONTACT", payload: data.contact });
			}
		};

		handleConnect();
		socket.on("connect", handleConnect);
		socket.on("ticket", handleTicket);
		socket.on("appMessage", handleAppMessage);
		socket.on("ticketListUpdate", handleTicketListUpdate);
		socket.on("contact", handleContact);

		return () => {
			if (status) socket.emit("leaveTickets", status);
			socket.off("connect", handleConnect);
			socket.off("ticket", handleTicket);
			socket.off("appMessage", handleAppMessage);
			socket.off("ticketListUpdate", handleTicketListUpdate);
			socket.off("contact", handleContact);
			socket.disconnect();
		};
	}, [status, searchParam, showAll, user, selectedQueueIds, unassigned]);

	useEffect(() => {
    if (typeof updateCount === "function") {
      updateCount(ticketsList.length);
    }
  }, [ticketsList.length, updateCount]);

	const loadMore = () => {
		setPageNumber(prevState => prevState + 1);
	};

	const handleScroll = e => {
		if (!hasMore || loading) return;

		const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;

		if (scrollHeight - (scrollTop + 100) < clientHeight) {
			e.currentTarget.scrollTop = scrollTop - 100;
			loadMore();
		}
	};

	return (
    <Paper className={classes.ticketsListWrapper} style={style}>
			{/* Barra de seleção em massa */}
			{selectedIds.size > 0 && (
				<div className={classes.bulkBar}>
					<span className={classes.bulkCount}>{selectedIds.size} selecionado(s)</span>
					<Button size="small" variant="contained" color="primary" disabled={bulkLoading} onClick={handleBulkResolve}>
						Resolver
					</Button>
					<Button size="small" variant="outlined" disabled={bulkLoading} onClick={() => setBulkTransferOpen(true)}>
						Transferir
					</Button>
					<Tooltip title="Cancelar seleção">
						<IconButton size="small" onClick={clearSelection}><CloseIcon fontSize="small" /></IconButton>
					</Tooltip>
				</div>
			)}
			<TransferTicketModal
				modalOpen={bulkTransferOpen}
				onClose={() => { setBulkTransferOpen(false); clearSelection(); }}
				ticketid={[...selectedIds][0]}
				ticketWhatsappId={null}
				ticketUserId={null}
				bulkIds={[...selectedIds]}
			/>
			<Paper
				square
				name="closed"
				elevation={0}
				className={classes.ticketsList}
				onScroll={handleScroll}
			>
				<List style={{ paddingTop: 0 }}>
					{ticketsList.length === 0 && !loading ? (
						<div className={classes.noTicketsDiv}>
							<span className={classes.noTicketsTitle}>
								{i18n.t("ticketsList.noTicketsTitle")}
							</span>
							<p className={classes.noTicketsText}>
								{i18n.t("ticketsList.noTicketsMessage")}
							</p>
						</div>
					) : (
						<>
							{ticketsList.map(ticket => (
								<TicketListItem
									ticket={ticket}
									key={ticket.id}
									isSelected={selectedIds.has(ticket.id)}
									onSelect={toggleSelect}
								/>
							))}
						</>
					)}
					{loading && <TicketsListSkeleton />}
				</List>
			</Paper>
    </Paper>
	);
};

export default TicketsList;
